import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Phone, Tag, User as UserIcon, Inbox } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { formatRelative } from '../lib/format';
import type { TicketStatus, TicketPriority } from '@vipsoft/shared-types';

interface Customer {
  id: number;
  external_id: string | null;
  source_platform: 'api' | 'email' | 'whatsapp' | 'telegram' | 'widget';
  email: string | null;
  phone: string | null;
  name: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface CustomerTicket {
  id: number;
  code: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  channel: string;
  assignee_id: number | null;
  assignee_name: string | null;
  created_at: string;
  closed_at: string | null;
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Nuevo',
  open: 'Abierto',
  in_progress: 'En proceso',
  waiting_customer: 'Esperando cliente',
  resolved: 'Resuelto',
  closed: 'Cerrado',
  reopened: 'Reabierto',
};

const STATUS_STYLE: Record<TicketStatus, string> = {
  new: 'text-slate-700 bg-slate-100',
  open: 'text-blue-700 bg-blue-50',
  in_progress: 'text-indigo-700 bg-indigo-50',
  waiting_customer: 'text-amber-700 bg-amber-50',
  resolved: 'text-emerald-700 bg-emerald-50',
  closed: 'text-slate-500 bg-slate-100',
  reopened: 'text-orange-700 bg-orange-50',
};

const PLATFORM_LABEL: Record<Customer['source_platform'], string> = {
  api: 'API',
  email: 'Correo',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  widget: 'Widget',
};

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [tickets, setTickets] = useState<CustomerTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    Promise.all([
      apiFetch<Customer>(`/customers/${id}`),
      apiFetch<CustomerTicket[]>(`/customers/${id}/tickets`),
    ])
      .then(([c, t]) => {
        setCustomer(c);
        setTickets(t);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="py-16 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando cliente…
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10 text-center text-sm text-rose-600">{error}</div>
    );
  }

  if (!customer) return null;

  const ticketsOpen = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed').length;
  const ticketsClosed = tickets.length - ticketsOpen;

  return (
    <div>
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={14} /> Volver a clientes
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ficha del cliente */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                <UserIcon size={24} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900 truncate">{customer.name}</h2>
                <p className="text-xs text-slate-500">
                  Registrado {formatRelative(customer.created_at)}
                </p>
              </div>
            </div>

            <dl className="space-y-3 text-sm">
              {customer.email && (
                <FieldRow icon={<Mail size={14} />} label="Correo">
                  <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline">
                    {customer.email}
                  </a>
                </FieldRow>
              )}
              {customer.phone && (
                <FieldRow icon={<Phone size={14} />} label="Teléfono">
                  {customer.phone}
                </FieldRow>
              )}
              <FieldRow icon={<Tag size={14} />} label="Origen">
                <span className="capitalize">{PLATFORM_LABEL[customer.source_platform]}</span>
              </FieldRow>
              {customer.external_id && (
                <FieldRow icon={<Tag size={14} />} label="ID externo">
                  <code className="text-xs font-mono text-slate-600 break-all">
                    {customer.external_id}
                  </code>
                </FieldRow>
              )}
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Resumen</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Tickets abiertos" value={ticketsOpen} tone="warn" />
              <Stat label="Tickets cerrados" value={ticketsClosed} tone="ok" />
            </div>
          </div>
        </div>

        {/* Historial de tickets */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-2xl">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900">Historial de tickets</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Todos los tickets creados por este cliente, del más reciente al más antiguo.
              </p>
            </div>
            {tickets.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-slate-500">
                <Inbox className="mx-auto mb-2 text-slate-300" size={32} />
                Este cliente todavía no ha generado tickets.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/70">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-2.5 font-medium">Ticket</th>
                      <th className="px-4 py-2.5 font-medium">Estado</th>
                      <th className="px-4 py-2.5 font-medium">Asignado a</th>
                      <th className="px-4 py-2.5 font-medium">Creado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tickets.map((t) => (
                      <tr key={t.id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-2.5">
                          <Link
                            to={`/admin/tickets/${t.code}`}
                            className="font-medium text-slate-900 hover:text-blue-600 truncate block max-w-[300px]"
                          >
                            {t.subject}
                          </Link>
                          <div className="text-[11px] text-slate-500 mt-0.5">{t.code}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[t.status]}`}
                          >
                            {STATUS_LABEL[t.status]}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-700 text-xs">
                          {t.assignee_name || <span className="text-slate-400">Sin asignar</span>}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">
                          {formatRelative(t.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <div className="text-slate-400 mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-slate-500">{label}</dt>
        <dd className="text-slate-900 mt-0.5 break-words">{children}</dd>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'warn';
}) {
  const toneClass =
    tone === 'ok' ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50';
  return (
    <div className={`px-3 py-2 rounded-lg ${toneClass}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}
